import { dateFromISOString } from "@prismicio/editor-support/Date"
import {
  Box,
  DateInput,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
} from "@prismicio/editor-ui"
import type { TimestampContent } from "@prismicio/types-internal/lib/content"
import type { Timestamp } from "@prismicio/types-internal/lib/customtypes"

import { getFieldLabel } from "./utils"

interface Props {
  id: string
  readOnly: boolean
  field: Timestamp
  content?: TimestampContent
  onContentChange: (content?: TimestampContent) => void
}

export function TimestampField(props: Props) {
  const { id, readOnly, field, content, onContentChange } = props

  const value = content?.value ? dateFromISOString(content.value) : undefined

  return (
    <Field>
      <FieldIcon name="event" />
      <FieldLabel id={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        <Box
          padding={{ left: 4, right: 12 }}
          width="100%"
          flexDirection="column"
          justifyContent="center"
        >
          {/* Link to React-Aria Library Issue for DateTimes //https://github.com/adobe/react-spectrum/issues/4750 */}
          <DateInput
            mode="date-time"
            aria-labelledby={id}
            disabled={readOnly}
            value={value}
            onValueChange={(newValue) => {
              onContentChange(updateContent(newValue))
            }}
          />
        </Box>
      </FieldControl>
    </Field>
  )
}

function updateContent(value?: Date): TimestampContent | undefined {
  if (!value) return undefined

  return {
    __TYPE__: "FieldContent",
    type: "Timestamp",
    value: toPrismicTimestampValue(value),
  }
}

// Historically, a prismic timestamp value is a very specific subset of ISO 8601 dates.
// It validates and reject any other valid ISO strings, like ones having a Z as a timezone, etc.
function toPrismicTimestampValue(date: Date): string {
  const YYYY = `${date.getUTCFullYear()}`.padStart(4, "0")
  const MM = `${date.getUTCMonth() + 1}`.padStart(2, "0")
  const DD = `${date.getUTCDate()}`.padStart(2, "0")
  const hh = `${date.getUTCHours()}`.padStart(2, "0")
  const mm = `${date.getUTCMinutes()}`.padStart(2, "0")

  return `${YYYY}-${MM}-${DD}T${hh}:${mm}:00+00:00`
}
